// What the order receipt (CustomerDetail.jsx → OrderReceipt) says that is
// arithmetic or matching rather than layout. Kept out of the JSX so it can be
// pinned by test/order-receipt.test.js without a DOM.
//
// An order and the money for it live in two systems. The Salesforce Order has
// the lines (OrderItems: product, quantity, unit and line price) and the
// PaymentIntent id stamped at fulfilment (Payment_Intent_ID__c). Stripe has
// the charge and its refunds, which reach the CRM through
// /api/accounts/:id/charges. A receipt is the two joined on that id — and
// every place they fail to join is something the rep should be told, not
// something to paper over with the order's own total.

// Money as integer cents, so a sum of lines can be compared to a total
// exactly. null for anything that is not a number: unknown is not $0.
export function toCents(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

// One row per OrderItem. lineCents prefers Salesforce's own TotalPrice and only
// multiplies out when that is missing, so a line discounted in Salesforce is
// shown at what it actually cost. `detail` is the line Description when it
// says something the product name does not (a domain, a term, a seat count).
export function receiptLines(order) {
  return (order?.OrderItems?.records || []).map(i => {
    const qty = Number.isFinite(Number(i.Quantity)) ? Number(i.Quantity) : null;
    const unitCents = toCents(i.UnitPrice);
    const total = toCents(i.TotalPrice);
    const lineCents = total !== null ? total
      : (unitCents !== null && qty !== null ? Math.round(unitCents * qty) : null);
    const name = i.Product2?.Name || i.Description || 'Item';
    const detail = i.Description && i.Description !== name ? i.Description : null;
    return {
      id: i.Id,
      name,
      detail,
      code: i.Product2?.ProductCode || null,
      domain: i.Domain_Name__c || null,
      qty,
      unitCents,
      lineCents,
    };
  });
}

export function findCharge(order, charges) {
  const pi = order?.Payment_Intent_ID__c;
  if (!pi || !Array.isArray(charges)) return null;
  return charges.find(c => c.paymentIntentId === pi) || null;
}

// Other orders on the account paid by the same PaymentIntent. One checkout can
// fulfil into more than one Order, and then the charge is bigger than any one
// of them for a reason that is not an error.
export function ordersSharingPayment(order, orders) {
  const pi = order?.Payment_Intent_ID__c;
  if (!pi || !Array.isArray(orders)) return [];
  return orders.filter(o => o.Id !== order.Id && o.Payment_Intent_ID__c === pi);
}

// The money block. Refunds come from the charge when we have it (it carries
// each refund), otherwise from the order's own refundedCents, which the orders
// route reads from the same billing ledger.
export function receiptTotals(order, charge) {
  const lines = receiptLines(order);
  const orderCents = toCents(order?.TotalAmount);
  const linesCents = lines.length && lines.every(l => l.lineCents !== null)
    ? lines.reduce((s, l) => s + l.lineCents, 0)
    : null;
  const chargedCents = charge && Number.isFinite(charge.amountCents) ? charge.amountCents : null;
  const refundedCents = charge && Number.isFinite(charge.refundedCents)
    ? charge.refundedCents
    : (Number.isFinite(order?.refundedCents) ? order.refundedCents : 0);
  const basis = chargedCents !== null ? chargedCents : orderCents;
  const netCents = basis === null ? null : basis - refundedCents;
  return { orderCents, linesCents, chargedCents, refundedCents, netCents };
}

// How the charge was paid: "Visa •••• 4242", or "Link" for a Link payment
// with no card behind it. null when the backend did not say — older backends
// send none of card / paymentMethodType, and absent is not "no card".
export function paymentMethodText(charge) {
  const card = charge?.card;
  if (card?.last4) {
    const brand = card.brand
      ? String(card.brand).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : 'Card';
    return `${brand} •••• ${card.last4}`;
  }
  if (!card && charge?.paymentMethodType === 'link') return 'Link';
  return null;
}

// Stripe's hosted receipt for the charge. Only an https URL becomes a link, so
// nothing the backend passes through can turn into javascript: or plain http.
export function stripeReceiptHref(charge) {
  const u = charge?.receiptUrl;
  return typeof u === 'string' && /^https:\/\//i.test(u) ? u : null;
}

const usd = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

// Everything about this order the rep should not have to notice for
// themselves. `tone` is 'warn' when a number on the receipt is in doubt and
// 'info' when it is only context.
//
// `charges` is the list we searched (null when it never loaded), and
// `chargesError` why it did not. A charge we could not find is NOT an unpaid
// order: the list is the latest N per contact email, and the payer may not be
// a contact on this account at all.
export function receiptNotes(order, { charge, charges, chargesError, siblings = [] } = {}) {
  const notes = [];
  const pi = order?.Payment_Intent_ID__c;
  const { orderCents, linesCents, chargedCents } = receiptTotals(order, charge);

  if (!pi) {
    notes.push(orderCents === 0
      ? { tone: 'info', text: 'A $0 order — nothing was charged, so there is no payment to show.' }
      : { tone: 'warn', text: 'No payment is linked to this order (it has no PaymentIntent). It may have been paid outside checkout, or placed before payments were linked to orders. Check Transactions before telling the customer anything about it.' });
  } else if (chargesError) {
    notes.push({ tone: 'warn', text: `Payment details did not load (${chargesError}). They are unknown, not missing.` });
  } else if (charges && !charge) {
    notes.push({ tone: 'warn', text: `Payment ${pi} is not among the recent charges for this account's contacts. It may be older, or paid with an email that is not a contact here.` });
  }

  if (charge && charge.status !== 'succeeded') {
    notes.push({ tone: 'warn', text: `The payment is "${charge.status}", not succeeded — this order has not been paid.` });
  }

  if (chargedCents !== null && orderCents !== null && chargedCents !== orderCents) {
    const siblingCents = siblings.reduce((s, o) => s + (toCents(o.TotalAmount) || 0), 0);
    if (siblings.length && chargedCents === orderCents + siblingCents) {
      notes.push({ tone: 'info', text: `The ${usd(chargedCents)} payment also paid for ${siblings.map(o => o.OrderNumber || 'another order').join(', ')}; this order's share is ${usd(orderCents)}.` });
    } else {
      notes.push({ tone: 'warn', text: `Charged ${usd(chargedCents)} but the order totals ${usd(orderCents)}.${siblings.length ? ` The same payment also covers ${siblings.map(o => o.OrderNumber || 'another order').join(', ')}.` : ''}` });
    }
  } else if (siblings.length) {
    notes.push({ tone: 'info', text: `The same payment also covers ${siblings.map(o => o.OrderNumber || 'another order').join(', ')}.` });
  }

  if (linesCents !== null && orderCents !== null && linesCents !== orderCents) {
    notes.push({ tone: 'warn', text: `The lines add up to ${usd(linesCents)} but the order total is ${usd(orderCents)}.` });
  }

  return notes;
}
